import { useCallback } from 'react'
import type { MeetingInformationDataTransferObject } from '@mobi/api-types'
import type { BetSource, RaceDetails } from '@mobi/betslip/types'
import {
  trackPreviewTipsBetClicked,
  trackRacePreviewBetClicked,
  trackTipsBetClicked,
} from '@core/Areas/Racing/Components/BettableTips/analytics'
import { makeSelection } from '@core/Areas/Racing/Components/BettableTips/helpers'
import type { Acceptor } from '@core/Areas/Racing/Hooks/useAcceptors'
import { RaceStatus } from '@core/Areas/Racing/Types'
import { PriceSources } from '@core/Data/Betting/selections'
import { handleBetSelection } from '@core/Utils/betting/handleBetSelection'
import { useFeature } from '@core/Utils/hooks'

type Props = {
  meeting: MeetingInformationDataTransferObject
  acceptor: Acceptor
  isFixedOdds: boolean
  priceSource: PriceSources
  buttonPosition: number
  tipSource: BetSource
  author?: string
}

export function useBettableTipQuickBetting({
  meeting,
  acceptor,
  isFixedOdds,
  priceSource,
  buttonPosition,
  tipSource,
  author,
}: Props) {
  const isNewPreviewTipsLayoutActive = useFeature('new-preview-tips-layout')

  const loadQuickBet = useCallback(() => {
    const selectedRace = meeting.SelectedRace

    const [bettingType, selection] = makeSelection(meeting, acceptor, isFixedOdds, priceSource)

    const shouldAllowPlaceInvestment = isFixedOdds
      ? (meeting.SelectedRace.FixedOddsInfo?.IsPlaceAvailable ?? false)
      : meeting.SelectedRace.HasPlacePool

    handleBetSelection({
      betFlow: { location: 'Quickbet' },
      selections: [
        {
          shouldAllowPlaceInvestment,
          isEachWayAvailable: false,
          bettingType,
          selection,
          selectionDetails: {
            acceptors: [acceptor],
            races: [
              {
                meetingDate: meeting.MeetingDate,
                raceTime: selectedRace.RaceTime,
                acceptorKeys: [acceptor.key],
                raceNumber: selectedRace.RaceNumber,
                key: selectedRace.Key,
                type: selectedRace.RaceType,
                distance: selectedRace.Distance,
                isFixedOddsRace: selectedRace.IsFixedOddsRace,
                meetingCode: meeting.MeetingCode,
                meetingId: meeting.MeetingId,
                meetingName: meeting.MeetingName,
              },
            ],
          } as RaceDetails,
          betSource: tipSource,
        },
      ],
    })

    if (isNewPreviewTipsLayoutActive) {
      const betType = 'type' in selection ? selection.type : undefined
      trackPreviewTipsBetClicked(
        meeting.MeetingName,
        author || '',
        meeting.MeetingCode,
        selectedRace.RaceStatus as RaceStatus,
        betType
      )
    } else {
      const trackFn = tipSource === 'tips' ? trackTipsBetClicked : trackRacePreviewBetClicked
      trackFn(meeting.MeetingCode, meeting.MeetingName, buttonPosition, 'Win & Place')
    }
  }, [
    meeting,
    acceptor,
    isFixedOdds,
    priceSource,
    tipSource,
    author,
    isNewPreviewTipsLayoutActive,
    buttonPosition,
  ])

  return {
    loadQuickBet,
  }
}
